import {
  createUnvalidatedValidationSummary,
  safeParseCoasterProject,
  type AuthoredGridPose,
  type CoasterProject,
  type TrackBehavior,
} from '../schema/index.ts';
import { newStableId } from '../editor/operations.ts';
import { getAuthoredDescriptor } from './catalog.ts';
import {
  descriptorExitPose,
  fixedPointToWorld,
  instantiateSpans,
  posesConnect,
  posesEqual,
} from './fixed-grid.ts';
import {
  AUTHORED_CATALOG_VERSION,
  type AuthoredBuildResult,
  type AuthoredPieceCandidate,
  type AuthoredPieceDescriptor,
  type AuthoredTrackType,
} from './types.ts';
import { materializeAuthoredProject } from './materialize.ts';

function behaviorFor(type: AuthoredTrackType): TrackBehavior {
  switch (type) {
    case 'station':
      return { kind: 'station', holdSeconds: 1.5, dispatchSpeedMps: 5 };
    case 'lift':
      return { kind: 'lift', targetSpeedMps: 5 };
    case 'launch':
      // A useful but broadly rideable V2 default: 30 mph. Creators can tune the
      // section after the circuit is complete without changing its authored shape.
      return { kind: 'launch', targetSpeedMps: 13.4112, accelerationMps2: 7 };
    case 'brake':
      return { kind: 'brake', targetSpeedMps: 9, decelerationMps2: 4 };
    case 'photo':
      return { kind: 'photo' };
    default:
      return { kind: 'normal' };
  }
}

function descriptorAllowsEntry(
  descriptor: AuthoredPieceDescriptor,
  pose: AuthoredGridPose,
): string | null {
  if (!descriptor.entry.pitches.includes(pose.pitchDegrees))
    return `${descriptor.label} cannot begin on a ${pose.pitchDegrees}° track.`;
  if (!descriptor.entry.banks.includes(pose.bank))
    return `${descriptor.label} requires ${descriptor.entry.banks.join(' or ')} banking.`;
  if (!descriptor.entry.orientations.includes(pose.orientation))
    return `${descriptor.label} requires ${descriptor.entry.orientations.join(' or ')} track.`;
  return null;
}

export function authoredEndPose(project: CoasterProject): AuthoredGridPose | null {
  if (project.track.geometryKind !== 'authored_grid_v2') return null;
  return project.track.pieces.at(-1)?.exitPose ?? null;
}

export function authoredSequenceIssue(
  project: CoasterProject,
  descriptorId: string,
): string | null {
  if (project.track.geometryKind !== 'authored_grid_v2') return null;
  const previousId = project.track.pieces.at(-1)?.descriptorId;
  if (!previousId) return null;
  const previous = getAuthoredDescriptor(previousId);
  const next = getAuthoredDescriptor(descriptorId);
  if (previous?.family === 'half_loop' && next?.family === 'half_loop')
    return 'Half Loops cannot connect directly because their planar paths would overlap. Continue inverted with a Half Roll or Half Corkscrew.';
  return null;
}

export function createAuthoredCandidate(
  descriptorId: string,
  entryPose: AuthoredGridPose,
  trackType: AuthoredTrackType = 'normal',
): AuthoredBuildResult<AuthoredPieceCandidate> {
  const descriptor = getAuthoredDescriptor(descriptorId);
  if (!descriptor)
    return {
      ok: false,
      code: 'unknown_descriptor',
      message: 'That track piece is not available in this catalog version.',
    };
  const entryFailure = descriptorAllowsEntry(descriptor, entryPose);
  if (entryFailure) return { ok: false, code: 'incompatible_entry', message: entryFailure };
  if (!descriptor.allowedTrackTypes.includes(trackType))
    return {
      ok: false,
      code: 'incompatible_track_type',
      message: `${descriptor.label} cannot use ${trackType} track.`,
    };
  return {
    ok: true,
    value: {
      descriptor,
      entryPose,
      exitPose: descriptorExitPose(entryPose, descriptor),
      spans: instantiateSpans(entryPose, descriptor),
      behavior: behaviorFor(trackType),
    },
  };
}

function resetDerivedProjectState(project: CoasterProject): CoasterProject {
  return {
    ...project,
    revisionId: newStableId('revision'),
    metadata: { ...project.metadata, updatedAt: new Date().toISOString() },
    validation: createUnvalidatedValidationSummary(),
    highlights: [],
  };
}

export function appendAuthoredCandidate(
  project: CoasterProject,
  candidate: AuthoredPieceCandidate,
): AuthoredBuildResult<CoasterProject> {
  if (project.track.geometryKind !== 'authored_grid_v2')
    return {
      ok: false,
      code: 'wrong_geometry_kind',
      message: 'This operation is only available in the V2 authored-grid builder.',
    };
  const currentEnd = authoredEndPose(project);
  if (currentEnd && !posesEqual(currentEnd, candidate.entryPose))
    return {
      ok: false,
      code: 'entry_mismatch',
      message: 'The preview no longer matches the current track exit.',
    };
  if (!currentEnd && project.track.pieces.length !== 0)
    return {
      ok: false,
      code: 'damaged_piece_list',
      message: 'The authored piece list has no valid track exit.',
    };
  const sequenceIssue = authoredSequenceIssue(project, candidate.descriptor.id);
  if (sequenceIssue)
    return {
      ok: false,
      code: 'incompatible_sequence',
      message: sequenceIssue,
    };

  const pieceId = newStableId('authored-piece');
  const startingNodeId = project.track.nodes.at(-1)?.id ?? newStableId('authored-node');
  const nodes = [...project.track.nodes];
  if (!nodes.length)
    nodes.push({
      id: startingNodeId,
      position: fixedPointToWorld(candidate.entryPose),
      bankDegrees: candidate.spans[0]?.bankStartDegrees ?? 0,
      rollWindingDegrees: candidate.spans[0]?.windingStartDegrees ?? 0,
    });

  const sections = [...project.track.sections];
  const sectionIds: string[] = [];
  let fromNodeId = startingNodeId;
  candidate.spans.forEach((span) => {
    const nodeId = newStableId('authored-node');
    const sectionId = newStableId('authored-section');
    nodes.push({
      id: nodeId,
      position: fixedPointToWorld(span.end),
      bankDegrees: Math.max(-90, Math.min(90, span.bankEndDegrees)),
      rollWindingDegrees: span.windingEndDegrees,
    });
    sections.push({
      id: sectionId,
      fromNodeId,
      toNodeId: nodeId,
      behavior: structuredClone(candidate.behavior),
      geometry: {
        kind: 'cubic_bezier',
        control1: fixedPointToWorld(span.control1),
        control2: fixedPointToWorld(span.control2),
      },
    });
    sectionIds.push(sectionId);
    fromNodeId = nodeId;
  });

  const next = resetDerivedProjectState({
    ...project,
    track: {
      ...project.track,
      nodes,
      sections,
      pieces: [
        ...project.track.pieces,
        {
          id: pieceId,
          descriptorId: candidate.descriptor.id,
          catalogVersion: AUTHORED_CATALOG_VERSION,
          entryPose: candidate.entryPose,
          exitPose: candidate.exitPose,
          sectionIds,
          spans: candidate.spans.map((span) => ({ ...span })),
          behavior: structuredClone(candidate.behavior),
        },
      ],
    },
    editor: {
      ...project.editor,
      pieceGroups: [
        ...project.editor.pieceGroups,
        {
          id: pieceId,
          definitionId: 'custom',
          sectionIds,
          parameters: null,
        },
      ],
    },
  });
  const parsed = safeParseCoasterProject(materializeAuthoredProject(next));
  if (!parsed.success)
    return {
      ok: false,
      code: 'schema_rejected_candidate',
      message: `The authored piece failed project validation: ${parsed.error.issues[0]?.message ?? 'unknown schema issue'}`,
    };
  const authoredTrack =
    parsed.data.track.geometryKind === 'authored_grid_v2' ? parsed.data.track : null;
  const firstPiece = authoredTrack?.pieces[0] ?? null;
  const lastPiece = authoredTrack?.pieces.at(-1) ?? null;
  if (
    firstPiece?.descriptorId === 'station.2t' &&
    lastPiece &&
    authoredTrack &&
    authoredTrack.pieces.length >= 4 &&
    posesConnect(lastPiece.exitPose, firstPiece.entryPose)
  )
    return closeAuthoredCircuit(parsed.data);
  return { ok: true, value: parsed.data };
}

export function appendAuthoredPiece(
  project: CoasterProject,
  descriptorId: string,
  entryPose: AuthoredGridPose | null = authoredEndPose(project),
  trackType: AuthoredTrackType = 'normal',
): AuthoredBuildResult<CoasterProject> {
  if (!entryPose)
    return {
      ok: false,
      code: 'station_required',
      message: 'Place a station before adding track.',
    };
  const candidate = createAuthoredCandidate(descriptorId, entryPose, trackType);
  return candidate.ok ? appendAuthoredCandidate(project, candidate.value) : candidate;
}

export function removeLastAuthoredPiece(
  project: CoasterProject,
): AuthoredBuildResult<CoasterProject> {
  if (project.track.geometryKind !== 'authored_grid_v2')
    return {
      ok: false,
      code: 'wrong_geometry_kind',
      message: 'This operation is only available in the V2 authored-grid builder.',
    };
  const piece = project.track.pieces.at(-1);
  if (!piece)
    return { ok: false, code: 'empty_track', message: 'There is no track piece to remove.' };
  const removeSections = new Set(piece.sectionIds);
  const remainingSections = project.track.sections.filter(
    (section) => !removeSections.has(section.id),
  );
  const remainingNodeIds = new Set<string>();
  remainingSections.forEach((section) => {
    remainingNodeIds.add(section.fromNodeId);
    remainingNodeIds.add(section.toNodeId);
  });
  const remainingPieces = project.track.pieces.slice(0, -1);
  if (!remainingSections.length && remainingPieces.length === 0) remainingNodeIds.clear();
  const next = resetDerivedProjectState({
    ...project,
    track: {
      ...project.track,
      closed: false,
      pieces: remainingPieces,
      sections: remainingSections,
      nodes: project.track.nodes.filter((node) => remainingNodeIds.has(node.id)),
    },
    editor: {
      ...project.editor,
      pieceGroups: project.editor.pieceGroups.filter((group) => group.id !== piece.id),
    },
  });
  const parsed = safeParseCoasterProject(materializeAuthoredProject(next));
  return parsed.success
    ? { ok: true, value: parsed.data }
    : {
        ok: false,
        code: 'schema_rejected_delete',
        message: 'The last piece could not be removed safely.',
      };
}

export function closeAuthoredCircuit(project: CoasterProject): AuthoredBuildResult<CoasterProject> {
  if (project.track.geometryKind !== 'authored_grid_v2')
    return {
      ok: false,
      code: 'wrong_geometry_kind',
      message: 'This operation is only available in the V2 authored-grid builder.',
    };
  const station = project.track.pieces[0];
  const end = project.track.pieces.at(-1);
  if (!station || station.descriptorId !== 'station.2t' || !end)
    return {
      ok: false,
      code: 'station_required',
      message: 'A circuit must begin with a station.',
    };
  if (!posesConnect(end.exitPose, station.entryPose))
    return {
      ok: false,
      code: 'not_aligned',
      message: 'The track exit must exactly match the station entry pose.',
    };
  if (project.track.nodes.length < 4)
    return {
      ok: false,
      code: 'too_short',
      message: 'Add more track before closing the circuit.',
    };
  const firstNode = project.track.nodes[0]!;
  const lastNode = project.track.nodes.at(-1)!;
  const sections = project.track.sections.map((section, index, source) =>
    index === source.length - 1 ? { ...section, toNodeId: firstNode.id } : section,
  );
  const nodes = project.track.nodes.filter((node) => node.id !== lastNode.id);
  const next = resetDerivedProjectState({
    ...project,
    track: { ...project.track, closed: true, nodes, sections },
  });
  const parsed = safeParseCoasterProject(materializeAuthoredProject(next));
  return parsed.success
    ? { ok: true, value: parsed.data }
    : {
        ok: false,
        code: 'schema_rejected_closure',
        message: 'The exact circuit could not be compiled.',
      };
}
